import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Gift, Package, Loader2 } from 'lucide-react';
import { GiftHamperDialog } from './GiftHamperDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HAMPER_IMAGES = {
  'hamper-1': 'https://images.pexels.com/photos/28769885/pexels-photo-28769885.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'hamper-2': 'https://images.unsplash.com/photo-1766375885762-41cfbacde261?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
  'hamper-3': 'https://images.unsplash.com/photo-1720788810305-85fe22e6c3a2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
  'hamper-4': 'https://images.pexels.com/photos/17878238/pexels-photo-17878238.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'hamper-5': 'https://images.unsplash.com/photo-1711645973621-fc4929ed9d5a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
};

export const GiftHampersSection = () => {
  const [hampers, setHampers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHamper, setSelectedHamper] = useState(null);

  useEffect(() => {
    const fetchHampers = async () => {
      try {
        const res = await axios.get(`${API}/gift-hampers`);
        setHampers(res.data);
      } catch (err) {
        console.error('Failed to fetch gift hampers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchHampers();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="gift-hampers-section">
      {/* Section header */}
      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-1.5 rounded-full text-sm font-medium mb-3">
          <Gift className="h-4 w-4" />
          Build Your Perfect Gift
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-accent mb-2">Curated Gift Hampers</h2>
        <p className="text-muted-foreground text-sm max-w-md mx-auto">
          Handpick the items inside — choose flavors and varieties to create a hamper your loved ones will adore.
        </p>
      </div>

      {/* Hamper cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="hampers-grid">
        {hampers.map((hamper) => (
          <Card
            key={hamper.id}
            className="group overflow-hidden border-amber-100 hover:border-amber-300 hover:shadow-lg transition-all duration-300 cursor-pointer"
            onClick={() => setSelectedHamper(hamper)}
            data-testid={`hamper-card-${hamper.id}`}
          >
            {/* Card image */}
            <div className="relative h-52 overflow-hidden">
              <img
                src={HAMPER_IMAGES[hamper.id]}
                alt={hamper.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3">
                <span className="bg-primary text-white text-xs font-bold px-2 py-1 rounded-full">
                  Starts at ₹{hamper.base_price}
                </span>
              </div>
            </div>

            {/* Card body */}
            <div className="p-4">
              <h3 className="font-bold text-accent text-lg mb-1">{hamper.name}</h3>
              <p className="text-sm text-muted-foreground mb-3">{hamper.tagline}</p>

              {/* Items preview */}
              <div className="flex flex-wrap gap-1 mb-4">
                {hamper.selection_groups.map((group) => (
                  <span
                    key={group.id}
                    className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full"
                  >
                    {group.options.length} {group.label.replace('Select ', '')} options
                  </span>
                ))}
              </div>

              <Button
                className="w-full bg-primary hover:bg-primary/90 rounded-full text-sm font-medium group-hover:shadow-md transition-all"
                onClick={(e) => { e.stopPropagation(); setSelectedHamper(hamper); }}
                data-testid={`customize-hamper-${hamper.id}`}
              >
                <Package className="h-4 w-4 mr-2" />
                Customize &amp; Add to Cart
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Dialog */}
      <GiftHamperDialog
        hamper={selectedHamper}
        open={!!selectedHamper}
        onClose={() => setSelectedHamper(null)}
      />
    </div>
  );
};
